// ui/pyreVeilLoreModal.js
// Pyre Veil atmospheric lore modal system with queue support

import { gameState } from '../game/gameState.js';
import { skillRegistry } from '../core/skillRegistry.js';
import { SKILLS } from '../game/constants.js';
import logger from '../utils/logger.js';

// Lore content definitions
export const PYRE_VEIL_LORE = {
    pyre_15: {
        id: 'pyre_15',
        title: 'The Veil Stirs',
        milestone: 15,
        content: `
            <p>The flames dance differently now. You've noticed it for days.</p>
            <p>Where once there was only heat and ash, you now see <em>shadows</em>
            moving between the flames. Shapes that shouldn't exist.</p>
            <p class="lore-whisper">"The veil grows thin for those who tend the sacred fire..."</p>
            <p>A merchant's tale comes to mind—stories of creatures that exist
            only in the space between flame and shadow. You dismissed them as
            superstition. Perhaps you were wrong.</p>
        `
    },
    pyre_35: {
        id: 'pyre_35',
        title: 'Echoes of the First Flame',
        milestone: 35,
        content: `
            <p>The fire speaks. Not in words, but in visions.</p>
            <p>You see ancient priests tending pyres that never died. Rituals
            performed in temples that no longer exist. And beneath it all,
            something <em>sealed away</em>.</p>
            <p class="lore-whisper">"The Cindered Reliquary... where the first flames were kindled..."</p>
            <p>The vision fades, but the knowledge remains. There are places
            in this world hidden from those who do not understand fire.</p>
        `
    },
    pyre_55: {
        id: 'pyre_55',
        title: 'The Ashbound',
        milestone: 55,
        content: `
            <p>You've begun to see them clearly now—creatures of living flame
            and solid ash. They watch you from the corners of every zone.</p>
            <p>They were always there. Waiting. Testing.</p>
            <p class="lore-whisper">"The worthy ones draw forth what was hidden..."</p>
            <p>The veil tears further. New prey emerges from realms between.
            Your mastery of the pyre has consequences.</p>
        `
    },
    pyre_80: {
        id: 'pyre_80',
        title: 'The Convergence Beckons',
        milestone: 80,
        content: `
            <p>The flames reveal their final secret.</p>
            <p>Beyond the Cindered Reliquary lies the Ashen Convergence—where
            all flames eventually return. Where power of unimaginable scale
            waits for those bold enough to claim it.</p>
            <p class="lore-whisper">"All fire is one fire. All ash returns to ash.
            The cycle completes in the Convergence..."</p>
            <p>You feel the pull. The ultimate challenge awaits.</p>
        `
    }
};

let modalElement = null;
let loreQueue = [];
let isShowingLore = false;
let onQueueComplete = null;

/**
 * Check which lore should trigger based on new level
 * @param {number} oldLevel - Previous Pyre level
 * @param {number} newLevel - New Pyre level
 * @returns {string[]} Array of lore IDs to show
 */
export function checkPyreLoreTriggers(oldLevel, newLevel) {
    const triggers = [];

    for (const lore of Object.values(PYRE_VEIL_LORE)) {
        // Check if milestone was crossed and not already seen
        if (oldLevel < lore.milestone && newLevel >= lore.milestone) {
            if (!gameState.hasSeenLore(lore.id)) {
                triggers.push(lore.id);
            }
        }
    }

    // Sort by milestone to show in order
    triggers.sort((a, b) => {
        const loreA = PYRE_VEIL_LORE[a];
        const loreB = PYRE_VEIL_LORE[b];
        return loreA.milestone - loreB.milestone;
    });

    return triggers;
}

/**
 * Show lore modal with queue support
 * @param {string} loreId - Lore identifier
 * @param {Function} onComplete - Callback when this modal closes
 */
export function showPyreVeilLore(loreId, onComplete = null) {
    const lore = PYRE_VEIL_LORE[loreId];
    if (!lore) {
        logger.warn(`[PYRE_VEIL] Unknown lore ID: ${loreId}`);
        if (onComplete) onComplete();
        return;
    }

    // Mark as seen immediately
    gameState.markLoreSeen(loreId);

    // Close any existing modal first
    closePyreVeilLore(true); // true = don't process queue yet

    // Create modal
    const pyreIcon = skillRegistry.getSkillIcon(SKILLS.PYRE);
    const modalContainer = document.createElement('div');
    modalContainer.className = 'pyre-veil-lore-overlay';
    modalContainer.innerHTML = `
        <div class="pyre-veil-lore-modal">
            <div class="lore-flame-border"></div>
            <div class="lore-header">
                <span class="lore-milestone">${pyreIcon} Pyre Level ${lore.milestone}</span>
                <h2 class="lore-title">${lore.title}</h2>
            </div>
            <div class="lore-content">
                ${lore.content}
            </div>
            <button class="lore-close-btn" onclick="window.pyreVeilLore.close()">
                Continue...
            </button>
        </div>
    `;

    modalElement = modalContainer;
    document.body.appendChild(modalElement);

    // Store callback
    onQueueComplete = onComplete;

    // Animate in
    requestAnimationFrame(() => {
        modalElement.classList.add('show');
    });

    // Handle escape key
    document.addEventListener('keydown', handleEscapeKey);

    logger.debug(`[PYRE_VEIL] Showing lore: ${lore.title}`);
}

/**
 * Handle escape key to close modal
 */
function handleEscapeKey(e) {
    if (e.key === 'Escape') {
        closePyreVeilLore();
    }
}

/**
 * Close current lore modal and process queue
 * @param {boolean} skipQueue - If true, don't process queue (for internal use)
 */
export function closePyreVeilLore(skipQueue = false) {
    if (!modalElement) {
        // No modal open, but still check queue if not skipping
        if (!skipQueue && loreQueue.length > 0) {
            const nextLoreId = loreQueue.shift();
            showPyreVeilLore(nextLoreId, onQueueComplete);
        }
        return;
    }

    // Remove escape key listener
    document.removeEventListener('keydown', handleEscapeKey);

    // Animate out
    modalElement.classList.remove('show');
    modalElement.classList.add('hide');

    setTimeout(() => {
        if (modalElement) {
            modalElement.remove();
            modalElement = null;
        }

        if (skipQueue) return;

        // Process next in queue or call completion callback
        if (loreQueue.length > 0) {
            const nextLoreId = loreQueue.shift();
            showPyreVeilLore(nextLoreId, onQueueComplete);
        } else {
            isShowingLore = false;
            if (onQueueComplete) {
                const callback = onQueueComplete;
                onQueueComplete = null;
                callback();
            }
        }
    }, 300);
}

/**
 * Queue and show multiple lore entries
 * @param {string[]} loreIds - Array of lore IDs to show
 * @param {Function} onAllComplete - Callback when all lore is shown
 */
export function showPyreVeilLoreQueue(loreIds, onAllComplete = null) {
    if (!loreIds || loreIds.length === 0) {
        if (onAllComplete) onAllComplete();
        return;
    }

    if (isShowingLore) {
        // Already showing, add to existing queue
        loreQueue.push(...loreIds);
        return;
    }

    isShowingLore = true;
    loreQueue = [...loreIds.slice(1)]; // All but first go to queue
    showPyreVeilLore(loreIds[0], onAllComplete);
}

/**
 * Check if lore is currently being shown
 * @returns {boolean}
 */
export function isLoreModalOpen() {
    return modalElement !== null;
}

/**
 * Get pending lore queue length
 * @returns {number}
 */
export function getLoreQueueLength() {
    return loreQueue.length;
}

// Expose to window for onclick handlers (browser only)
if (typeof window !== 'undefined') {
    window.pyreVeilLore = {
        show: showPyreVeilLore,
        close: closePyreVeilLore,
        showQueue: showPyreVeilLoreQueue,
        checkTriggers: checkPyreLoreTriggers,
        isOpen: isLoreModalOpen
    };
}

export default {
    PYRE_VEIL_LORE,
    showPyreVeilLore,
    closePyreVeilLore,
    showPyreVeilLoreQueue,
    checkPyreLoreTriggers,
    isLoreModalOpen,
    getLoreQueueLength
};
